import { getGraphQLAuthErrorCode } from '@opendoor/auth-fe/errors';
import { ClientError } from 'graphql-request';
import { z } from 'zod';

export type BookTourFailureReason =
  | 'slotUnavailable'
  | 'unauthorized'
  | 'unauthenticated'
  | 'reauthenticationRequired'
  | 'invalidInput'
  | 'timeout'
  | 'unknown';

const ZEUS_CODE_REASONS: Record<string, BookTourFailureReason> = {
  UNAUTHENTICATED: 'unauthenticated',
  REAUTHENTICATION_REQUIRED: 'reauthenticationRequired',
  FORBIDDEN: 'unauthorized',
  BAD_USER_INPUT: 'invalidInput',
  NOT_FOUND: 'slotUnavailable',
};

const errorEnvelopeSchema = z.array(
  z.object({ extensions: z.object({ code: z.string() }).partial().optional() }),
);

const zeusErrorCodes = (error: ClientError): string[] => {
  const parsed = errorEnvelopeSchema.safeParse(error.response.errors);
  if (!parsed.success) {
    return [];
  }
  return parsed.data.flatMap((entry) => (entry.extensions?.code ? [entry.extensions.code] : []));
};

export function bookTourFailureReason(error: unknown): BookTourFailureReason {
  // AbortSignal.timeout rejects with TimeoutError while the request outcome on the server stays
  // unknown, so callers must not treat it like an ordinary failure that is safe to retry.
  if (error instanceof DOMException && error.name === 'TimeoutError') {
    return 'timeout';
  }
  if (!(error instanceof ClientError)) {
    return 'unknown';
  }
  for (const code of zeusErrorCodes(error)) {
    const reason = bookTourFailureReasonFromResultCode(code);
    if (reason !== 'unknown') {
      return reason;
    }
  }
  return 'unknown';
}

export function bookTourFailureReasonFromResultCode(code: string): BookTourFailureReason {
  const authCode = getGraphQLAuthErrorCode({ code, message: '' });
  return ZEUS_CODE_REASONS[authCode ?? code] ?? 'unknown';
}
